import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Check } from 'lucide-react';
import { useUI } from '../contexts/UIContext';

export interface PriorityDropZonesProps {
  activeDragId: string | number | null;
  droppedZoneId?: string | null;
  filterPriority?: string;
  onToggleFilterPriority?: (priority: string) => void;
}

const PriorityZone: React.FC<{
  id: string;
  label: string;
  color: string;
  isActiveDrag: boolean;
  isDropped: boolean;
  isFilterActive: boolean;
  onToggleFilter?: (id: string) => void;
}> = ({ id, label, color, isActiveDrag, isDropped, isFilterActive, onToggleFilter }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `priority-${id}`,
  });

  const handleClick = (e: React.MouseEvent) => {
    if (!isActiveDrag && onToggleFilter) {
      e.stopPropagation();
      onToggleFilter(id);
    }
  };

  return (
    <div
      ref={setNodeRef}
      data-dropzone={`priority-${id}`}
      data-dropzone-over={isOver ? 'true' : 'false'}
      className="p-3 -m-3"
    >
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={isFilterActive}
        aria-label={
          isFilterActive
            ? `Prioritätsfilter ${label} aktiv – Klicken zum Aufheben`
            : `Nach Priorität ${label} filtern oder Aufgabe hier ablegen`
        }
        title={
          isFilterActive 
            ? `Priorität "${label}" aktiv – Klick zum Aufheben` 
            : isActiveDrag 
              ? `Aufgabe auf Priorität "${label}" ziehen` 
              : `Nach Priorität "${label}" filtern`
        }
        className={`
          flex items-center gap-2 rounded-xl transition-all duration-300 ease-out select-none border
          ${isActiveDrag ? 'cursor-grabbing' : 'cursor-pointer active:scale-95'}
          ${isActiveDrag 
            ? 'px-6 py-3.5 border-dashed border-indigo-400/40 dark:border-indigo-500/40 bg-indigo-50/30 dark:bg-indigo-900/10 scale-105 shadow-sm' 
            : isFilterActive
              ? 'px-4 py-2 border-indigo-500/50 dark:border-indigo-400/50 bg-indigo-500/15 dark:bg-indigo-500/20 shadow-xs ring-1 ring-indigo-500/20'
              : 'px-4 py-2 border-transparent bg-transparent hover:bg-slate-200/50 dark:hover:bg-slate-800/60 hover:border-slate-300/40 dark:hover:border-slate-700/40'
          }
          ${isOver 
            ? '!bg-indigo-100 dark:!bg-indigo-800 scale-120 !border-indigo-500 shadow-[0_0_20px_rgba(99,102,241,0.4)] z-20' 
            : ''}
          ${isDropped 
            ? '!bg-emerald-500 dark:!bg-emerald-600 !border-emerald-500 text-white scale-110 shadow-lg shadow-emerald-500/20 z-10 animate-pulse' 
            : ''}
        `}
      >
        {isDropped ? (
          <Check size={isActiveDrag ? 15 : 14} className="stroke-[3] text-white" />
        ) : (
          <div className={`rounded-full ${color} ${isOver ? 'animate-ping' : ''} transition-all duration-300 ${isFilterActive ? 'ring-2 ring-indigo-400/50 scale-110' : ''} ${isActiveDrag ? 'w-3 h-3' : 'w-2.5 h-2.5'}`} />
        )}
        <span className={`
          font-black uppercase tracking-wider transition-all duration-300
          ${isActiveDrag ? 'text-xs' : 'text-[10px]'}
          ${isDropped ? 'text-white' : isOver ? 'text-indigo-700 dark:text-indigo-200' : isFilterActive ? 'text-indigo-600 dark:text-indigo-300' : 'text-slate-400'}
        `}>
          {isDropped ? 'Zugewiesen' : isOver ? `Loslassen: Prio ${label}` : label}
        </span>
        {isFilterActive && !isActiveDrag && (
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse ml-0.5" />
        )}
      </button>
    </div>
  );
};

export const PriorityDropZones: React.FC<PriorityDropZonesProps> = ({ 
  activeDragId, 
  droppedZoneId,
  filterPriority: propFilterPriority,
  onToggleFilterPriority: propToggleFilter
}) => {
  const ui = useUI();
  const { setFilterPriority } = ui;
  const activePriority = propFilterPriority ?? ui.filterPriority ?? 'all';

  const handleToggle = React.useCallback((priorityId: string) => {
    if (propToggleFilter) {
      propToggleFilter(priorityId);
    } else if (setFilterPriority) {
      setFilterPriority((prev: string) => prev === priorityId ? 'all' : priorityId);
    }
  }, [propToggleFilter, setFilterPriority]);

  const isActiveDrag = activeDragId !== null;

  return (
    <>
      <PriorityZone 
        id="high" 
        label="Hoch" 
        color="bg-rose-500" 
        isActiveDrag={isActiveDrag} 
        isDropped={droppedZoneId === 'priority-high'}
        isFilterActive={activePriority === 'high'}
        onToggleFilter={handleToggle}
      />
      <div className="w-px h-4 bg-slate-200/50 dark:bg-slate-800/50" />
      <PriorityZone 
        id="medium" 
        label="Mittel" 
        color="bg-amber-500" 
        isActiveDrag={isActiveDrag} 
        isDropped={droppedZoneId === 'priority-medium'}
        isFilterActive={activePriority === 'medium'}
        onToggleFilter={handleToggle}
      />
      <div className="w-px h-4 bg-slate-200/50 dark:bg-slate-800/50" />
      <PriorityZone 
        id="low" 
        label="Niedrig" 
        color="bg-emerald-500" 
        isActiveDrag={isActiveDrag} 
        isDropped={droppedZoneId === 'priority-low'}
        isFilterActive={activePriority === 'low'}
        onToggleFilter={handleToggle}
      />
      <div className="w-px h-4 bg-slate-200/50 dark:bg-slate-800/50" />
      <PriorityZone 
        id="none" 
        label="Keine" 
        color="bg-slate-400" 
        isActiveDrag={isActiveDrag} 
        isDropped={droppedZoneId === 'priority-none'}
        isFilterActive={activePriority === 'none'}
        onToggleFilter={handleToggle}
      />
    </>
  );
};
